import { useState, useEffect } from "react";

const TameChaos = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const dataSources = [
    { icon: "📊", label: "Live perps analytics", color: "text-blue-400" },
    { icon: "📈", label: "Live spot analytics", color: "text-green-400" },
    { icon: "🎯", label: "Open position tracking", color: "text-orange-400" },
    { icon: "🤖", label: "AI trade mentor", color: "text-purple-400" },
    { icon: "📋", label: "Strategy analysis", color: "text-cyan-400" }
  ];

  return (
    <>
      <style>{`
        @keyframes travelRight {
          0% {
            left: 0%;
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            left: calc(100% - 6px);
            opacity: 0;
          }
        }
      `}</style>
      <section className="relative overflow-hidden border-y border-white/10 py-12 sm:py-16 lg:py-24 xl:py-28">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent"></div>
      
      <div className="relative mx-auto max-w-[1600px] px-4 sm:px-6">
        {/* Header */}
        <div className={`mb-10 sm:mb-12 lg:mb-16 xl:mb-20 text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="mb-3 sm:mb-2 text-3xl text-white sm:text-5xl">
            <span className="text-white font-bold font-space">Sharpen your edge.</span>
          </h2>
          <p className="mx-auto max-w-3xl text-base sm:text-lg lg:text-xl text-gray-400">
            Traders waste hours trading blindly with no analysis tracking, struggling to connect the dots. 
            <span className="text-white"> Sharpex brings everything together.</span>
          </p>
        </div>

        {/* Main Content */}
        <div className="relative">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-3 lg:gap-8">
            
            {/* Left Column - Data Sources */}
            <div className="relative flex flex-col justify-center">
              {/* Mobile connector line */}
              <div className="absolute left-1/2 top-0 w-[2px] -translate-x-1/2 lg:hidden bg-gradient-to-b from-white/20 to-transparent z-0">
                <div className="absolute left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-primary animate-pulse"></div>
              </div>
              
              <div className="space-y-4 relative z-10">
                {dataSources.map((source, index) => (
                  <div 
                    key={index}
                    className={`relative transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    {/* Desktop connector line */}
                    <div className="absolute left-full top-1/2 hidden h-[2px] w-full lg:block bg-gradient-to-r from-white/20 via-transparent to-transparent overflow-hidden">
                      <div 
                        className="absolute top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-primary animate-pulse"
                        style={{ animationDelay: `${index * 0.1}s` }}
                      ></div>
                      {/* Animated traveling dot */}
                      <div 
                        className="absolute top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-primary shadow-lg shadow-primary/50"
                        style={{
                          animation: `travelRight 2s ease-in-out infinite`,
                          animationDelay: `${index * 0.3}s`
                        }}
                      ></div>
                    </div>
                    
                    <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 p-4 backdrop-blur-sm">
                      <span className={`text-lg ${source.color}`}>{source.icon}</span>
                      <span className="text-sm text-gray-300">{source.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Center Column - Terminal */}
            <div className="flex h-full items-start justify-center pt-0 lg:pt-[11px]">
              <div className={`relative transition-all duration-1000 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{ zIndex: 10 }}>
                {/* Glow effect */}
                <div className="absolute inset-0 rounded-2xl bg-primary/20 blur-2xl animate-pulse"></div>
                
                {/* Terminal card */}
                <div className="relative rounded-2xl border-2 border-primary/50 bg-[#0d0d12] p-6 sm:px-8 sm:pt-16 sm:pb-16 sm:flex sm:flex-col sm:justify-center" style={{ zIndex: 10 }}>
                  {/* Logo */}
                  <div className="mb-3 sm:mb-4 flex justify-center">
                    <div className="h-12 w-12 sm:h-16 sm:w-16">
                      <img src="/sharpex-logo.png" alt="SharpeX Logo" className="block size-full" />
                    </div>
                  </div>
                  
                  {/* Title */}
                  <div className="text-center">
                    <h3 className="mb-1 sm:mb-2 text-lg sm:text-xl text-white font-bold">Sharpex Terminal</h3>
                    <p className="text-xs sm:text-sm text-gray-400">AI Analytics Engine</p>
                  </div>
                  
                  {/* Processing bars */}
                  <div className="mt-4 space-y-2">
                    <div className="h-1 rounded-full bg-gradient-to-r from-primary to-transparent animate-pulse" style={{ animationDelay: "0.3s" }}></div>
                    <div className="h-1 rounded-full bg-gradient-to-r from-primary to-transparent animate-pulse" style={{ animationDelay: "0.6s" }}></div>
                    <div className="h-1 rounded-full bg-gradient-to-r from-primary to-transparent animate-pulse" style={{ animationDelay: "0.9s" }}></div>
                  </div>
                </div>
                
                {/* Mobile flow lines */}
                <div className="absolute left-1/2 top-full w-[2px] -translate-x-1/2 lg:hidden bg-gradient-to-b from-primary/60 to-primary/30 overflow-hidden">
                  <div className="absolute inset-x-0 bg-gradient-to-b from-transparent via-blue-400 to-transparent animate-pulse" style={{ height: "30%", animationDelay: "0s" }}></div>
                  <div className="absolute inset-x-0 bg-gradient-to-b from-transparent via-orange-400 to-transparent animate-pulse" style={{ height: "35%", animationDelay: "0.6s" }}></div>
                  <div className="absolute inset-x-0 bg-gradient-to-b from-transparent via-green-400 to-transparent animate-pulse" style={{ height: "28%", animationDelay: "1.2s" }}></div>
                </div>
              </div>
            </div>

            {/* Right Column - Output */}
            <div className="flex flex-col justify-center">
              <div className={`relative transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} style={{ transitionDelay: "500ms" }}>
                {/* Input connector */}
                <div className="absolute right-full top-1/2 hidden h-[2px] lg:block" style={{ transform: "translateY(-14px)", width: "7.5rem", zIndex: 5 }}>
                  <div className="h-full origin-left bg-gradient-to-r from-white/10 via-white/20 to-primary/40 overflow-hidden">
                    <div className="absolute inset-y-0 animate-pulse bg-gradient-to-r from-transparent via-blue-400 to-transparent" style={{ width: "30%", animationDelay: "0s" }}></div>
                    <div className="absolute inset-y-0 animate-pulse bg-gradient-to-r from-transparent via-orange-400 to-transparent" style={{ width: "35%", animationDelay: "0.6s" }}></div>
                    <div className="absolute inset-y-0 animate-pulse bg-gradient-to-r from-transparent via-green-400 to-transparent" style={{ width: "28%", animationDelay: "1.2s" }}></div>
                  </div>
                </div>
                
                {/* Output card */}
                <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent p-6">
                  <div className="mb-4 flex items-center gap-2 border-b border-white/10 pb-3">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    <span className="text-xs text-gray-500">Sharpex Terminal</span>
                  </div>
                  
                  <div>
                    <div className="space-y-3">
                      {/* AI Insights Header */}
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-4 h-4 text-primary">💡</div>
                        <span className="text-sm font-medium text-white">AI-Generated Insights</span>
                      </div>
                      
                      {/* Sample Insights */}
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <span className="text-primary mt-0.5 text-xs">•</span>
                          <p className="text-xs text-gray-300">Your ETH positions show strong timing alpha with +$2,340 in execution gains</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-primary mt-0.5 text-xs">•</span>
                          <p className="text-xs text-gray-300">Consider reducing leverage during high volatility periods to minimize drawdown</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-primary mt-0.5 text-xs">•</span>
                          <p className="text-xs text-gray-300">Fee drag at 12.4 bps suggests excessive taker usage on SOL trades</p>
                        </div>
                      </div>
                      
                      {/* Sample Metrics */}
                      <div className="mt-4 pt-3 border-t border-white/10">
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div>
                            <span className="text-gray-400">Win Rate:</span>
                            <span className="ml-2 text-emerald-400 font-mono">62.3%</span>
                          </div>
                          <div>
                            <span className="text-gray-400">Avg PnL:</span>
                            <span className="ml-2 text-emerald-400 font-mono">+$47.80</span>
                          </div>
                          <div>
                            <span className="text-gray-400">Max DD:</span>
                            <span className="ml-2 text-rose-400 font-mono">-8.2%</span>
                          </div>
                          <div>
                            <span className="text-gray-400">Sharpe:</span>
                            <span className="ml-2 text-primary font-mono">1.84</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default TameChaos;
